import React from "react";
import { useLocation } from "react-router-dom";

import { faCommentAltExclamation, faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faCircle, faDotCircle, faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import ThemeContext from "~/common/ThemeContext";
import { ReactComponent as FellowWordmark } from "~/legacy-assets/images/logos/fellow-wordmark-black-solid.svg";
import { ConnectedIcon } from "~/stream/ConnectedIcon";
import { Avatar, Button, LinkButton, Tooltip } from "~/ui";
import IconButton from "~/ui/legacy/IconButton";
import { Input, Modal } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import { __globalModalContext, useModals } from "~/ui/modals";
import toast from "~/ui/toast";

import stylesButton from "~/ui/button/Button.module.css";

import styles from "./css/Header.module.css";

interface ReportModalProps {
	streamId: string;
	onClose: () => void;
}

type ReportReason = "SPAM" | "MALWARE" | "HATE" | "ILLEGAL" | "DCMA" | "OTHER";

function ReportModal({ streamId, onClose }: ReportModalProps) {
	const [reason, setReason] = React.useState<ReportReason>("SPAM");
	const [email, setEmail] = React.useState("");
	const [details, setDetails] = React.useState("");

	const onEmailChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setEmail(event.target.value);
		},
		[setEmail],
	);

	const onDetailsChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setDetails(event.target.value);
		},
		[setDetails],
	);

	const onSubmit = React.useCallback(() => {
		const data = new FormData();

		data.append("stream_id", streamId);
		data.append("reason", reason);
		data.append("email", email);
		data.append("details", details);

		fetch("/public/streams/report/", {
			method: "POST",
			body: data,
			headers: {
				"x-csrftoken": window.CSRF_TOKEN,
				"x-requested-with": "XMLHttpRequest",
			},
		})
			.then((res: Response) => {
				return res.json();
			})
			.then(({ ok }: { ok: boolean }) => {
				ok
					? toast.success("Thank you for making Fellow a better place ❤️", {
							title: "Your report has been received",
							autoClose: 5000,
						})
					: toast.error("Something went wrong while sending your report. Please try again in a few minutes.");
				onClose();
			})
			.catch(() => {
				toast.error("Something went wrong while sending your report. Please try again in a few minutes.");
				onClose();
			});
	}, [onClose, email, reason, streamId, details]);

	function onReasonClick(reason: ReportReason) {
		return () => {
			setReason(reason);
		};
	}

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose}>
				<Modal.Title>Report Note Series</Modal.Title>
			</Modal.Header>
			<Modal.Body className={styles.modalBody}>
				<h4>Help us understand the problem. What is wrong with these notes?</h4>
				<button
					className={cx(stylesButton.button, styles.reportOption, { [styles.selected]: reason === "SPAM" })}
					onClick={onReasonClick("SPAM")}
				>
					<FontAwesomeIcon icon={reason === "SPAM" ? faDotCircle : faCircle} />
					<span>
						It&apos;s likely <strong>spam</strong>
					</span>
				</button>
				<button
					className={cx(stylesButton.button, styles.reportOption, { [styles.selected]: reason === "MALWARE" })}
					onClick={onReasonClick("MALWARE")}
				>
					<FontAwesomeIcon icon={reason === "MALWARE" ? faDotCircle : faCircle} />
					<span>
						It contains <strong>suspicious links/malware</strong>
					</span>
				</button>
				<button
					className={cx(stylesButton.button, styles.reportOption, { [styles.selected]: reason === "HATE" })}
					onClick={onReasonClick("HATE")}
				>
					<FontAwesomeIcon icon={reason === "HATE" ? faDotCircle : faCircle} />
					<span>
						It&apos;s <strong>abusive or hateful</strong>
					</span>
				</button>
				<button
					className={cx(stylesButton.button, styles.reportOption, { [styles.selected]: reason === "ILLEGAL" })}
					onClick={onReasonClick("ILLEGAL")}
				>
					<FontAwesomeIcon icon={reason === "ILLEGAL" ? faDotCircle : faCircle} />
					<span>
						It contains (or links to) <strong>illegal material</strong>
					</span>
				</button>
				<button
					className={cx(stylesButton.button, styles.reportOption, { [styles.selected]: reason === "DCMA" })}
					onClick={onReasonClick("DCMA")}
				>
					<FontAwesomeIcon icon={reason === "DCMA" ? faDotCircle : faCircle} />
					<span>
						It <strong>violates a copyright/trademark</strong>
					</span>
				</button>
				<button
					className={cx(stylesButton.button, styles.reportOption, { [styles.selected]: reason === "OTHER" })}
					onClick={onReasonClick("OTHER")}
				>
					<FontAwesomeIcon icon={reason === "OTHER" ? faDotCircle : faCircle} />
					<span>
						Some <strong>other reason</strong>
					</span>
				</button>
				<div className={styles.reportInput}>
					<Input
						value={details}
						onChange={onDetailsChange}
						placeholder="Other details (optional)"
						name="report_details"
					/>
					<sup>Enter any details you feel are important</sup>
				</div>
				<div className={styles.reportInput}>
					<h5>Stay in the loop</h5>
					<Input value={email} onChange={onEmailChange} placeholder="Your email (optional)" name="report_email" />
					<sup>Get notified of any action taken on your report</sup>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" onClick={onClose}>
					Cancel
				</Button>
				<Button size="normal" variant="primary" onClick={onSubmit}>
					Submit
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

interface HeaderProps {
	streamId?: string;
	inviteId?: string;
	workspace?: {
		name: string;
		logo: string;
	};
}

export default function Header({ streamId, inviteId, workspace }: HeaderProps) {
	const modals = useModals();
	const openReportModal = React.useCallback(() => {
		if (!streamId) return;
		modals.open(({ close }) => <ReportModal onClose={close} streamId={streamId} />);
	}, [modals, streamId]);

	const { isDarkMode } = React.useContext(ThemeContext);

	const [tooltipVisible, setTooltipVisible] = React.useState(true);
	const dismissTooltip = React.useCallback(() => {
		setTooltipVisible(false);
	}, [setTooltipVisible]);

	const location = useLocation();

	let urlParams = "";
	if (inviteId) {
		const encodedStreamInviteId = encodeURIComponent(inviteId);
		const streamInviteNextUrl = encodeURIComponent(`/ext/notes/invite/${encodedStreamInviteId}/consume/`);
		urlParams = `?next=${streamInviteNextUrl}&stream_invite=${encodedStreamInviteId}`;
	} else {
		urlParams = `?next=${encodeURIComponent(location.pathname)}`;
	}

	const loginLink = `/auth/login/${urlParams}`;
	const signupLink = `/auth/signup/${urlParams}`;

	return (
		<header className={styles.header}>
			<div className={styles.container}>
				<div className={styles.workspace}>
					{workspace ? (
						<>
							<Avatar user={{ fullName: workspace.name, image: workspace.logo }} className={styles.logo} />
							{workspace.name}
						</>
					) : (
						<a href="https://fellow.app/" className={styles.wordMark}>
							<FellowWordmark />
						</a>
					)}
				</div>
				<div className={styles.actions}>
					{!inviteId && (
						<LinkButton to={loginLink} size="slim" variant="tertiary">
							Login
						</LinkButton>
					)}
					<Tooltip
						visible={tooltipVisible}
						disabled={!inviteId}
						appendTo={document.documentElement}
						interactive
						className={styles.tooltip}
						placement="bottom-end"
						content={
							<article>
								<header className={styles.tooltipHeader}>
									<h1>
										<ConnectedIcon inverted={!isDarkMode} className={styles.tooltipConnectedIcon} />
										Add to the agenda
									</h1>
									<FontAwesomeIcon icon={faTimes} className={styles.closeIcon} onClick={dismissTooltip} />
								</header>
								<main className={styles.tooltipBody}>
									<p>
										{window.INITIAL_STATE.invite_sender_name} wants to collaborate with you on this meeting in Fellow.
									</p>
								</main>
							</article>
						}
					>
						<LinkButton to={signupLink} size="slim" variant="primary">
							{inviteId ? "Add to the agenda" : "Try Fellow for free"}
						</LinkButton>
					</Tooltip>
					{streamId && (
						<Menu mobileHeaderTitle="Actions">
							<Menu.Trigger>
								<IconButton title="More" icon={faEllipsisV} />
							</Menu.Trigger>
							<Menu.Content align="end">
								<Menu.Item onSelect={openReportModal} icon={faCommentAltExclamation}>
									Report notes
								</Menu.Item>
							</Menu.Content>
						</Menu>
					)}
				</div>
			</div>
		</header>
	);
}
